<template>
  <div class="tournament">
    <div class="tournament__top-wrapper">
      <div class="image-wrapper">
        <img :src="getTournamentImage(info.name)" alt="trophy" class="tournament-card__image" />
      </div>
      <div class="tournament__info">
        <h1 class="tournament__info-title">
          {{ info.name }}
          {{ route.params.category ? t("filters.weapon." + getCategoryName()) : "" }}
        </h1>
        <p class="tournament__info-desc">
          {{ t("tournament_desc", { name: info.name }) }}
        </p>
      </div>
    </div>

    <div class="tournament__sort f">
      <div class="categories-wrapper">
        <PagesTounamentComponentsCategories
          :categories="categories"
          :link="'/tournaments/' + route.params.id"
        />
      </div>

      <ui-select
        v-model="orderBy"
        :items="orderByItems"
        label-key="label"
        value-key="value"
      ></ui-select>
    </div>
    <template v-if="items.length && !loading">
      <div class="tournament__grid">
        <LazyPagesTounamentComponentsTournamentItemCard
          v-for="item in props.items"
          :key="item.link"
          :item="item"
        />
      </div>
      <PaginateV3
        v-if="pagesLength > 1"
        :loading="loading"
        :pages-length="pagesLength"
        :total="itemsLength"
        :total-items="10 * page < itemsLength ? 10 * page : itemsLength"
        use-query
      />
    </template>

    <div v-else-if="loading" class="f fc w-100 py90">
      <i v-if="loading" class="i-loading" />
    </div>

    <LazyUiNoResult v-else class="mxa" />

    <NuxtPage />
  </div>
</template>

<script lang="ts" setup>
import { AdvancedSearchBody, OrderBy, TournamentInfo } from "~/graphql/generated/schema-types";

const props = defineProps<{
  items: AdvancedSearchBody[];
  info: TournamentInfo;
  categories: string[];
  loading: boolean;
  pagesLength: number;
  itemsLength: number;
}>();

const orderBy = defineModel("orderBy");

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const orderByItems = computed(() => {
  const sortedOrders = Object.values(OrderBy).filter(
    (el: string) => el !== "HIGHEST_FLOAT" && el !== "LOWEST_FLOAT"
  );
  return sortedOrders.map((val: string) => ({
    value: val,
    label: t("order_by." + val)
  }));
});

const page = computed(() => {
  return Number(route.query.page) || 1;
});
const getCategoryName = () => {
  return ((route.params.category as string) || "")
    .split("-")
    .map((i) => i.charAt(0).toUpperCase() + i.slice(1))
    .join(" ");
};
</script>

<style lang="scss" scoped>
.tournament {
  padding-top: 32px;
  padding-bottom: 80px;

  &__top-wrapper {
    display: flex;
    align-items: center;
    gap: 56px;
    margin-bottom: 40px;

    .image-wrapper {
      width: 200px;
      height: 200px;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        position: relative;
        z-index: 1;
      }

      &:after {
        content: "\e81f";
        font-family: "fontello";
        position: absolute;
        inset: 0;
        text-align: center;
        display: block;
        font-size: 15rem;
        line-height: 20rem;
        color: var(--surfaces-outline);
        filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
        opacity: 0.2;
      }
    }
  }

  &__sort {
    margin-bottom: 16px;
    flex-wrap: wrap;

    .categories-wrapper {
      overflow-x: auto;
    }

    &:deep(.cselect) {
      width: max-content;

      .cselect_top {
        min-width: 192px;
      }
    }
  }

  &__info {
    &-title {
      font-size: 28px;
      font-weight: 600;
      line-height: 36px;
      margin-bottom: 20px;
      color: var(--surfaces-on-surface);
    }

    &-desc {
      max-width: 600px;
      font-size: 16px;
      font-weight: 400;
      line-height: 32px;
      color: var(--surfaces-on-surface-variable);
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: 16px;
  }

  @media (max-width: 525px) {
    padding-bottom: 16px;
    &__top-wrapper {
      flex-direction: column;
      gap: 8px;
      margin-bottom: 24px;

      .image-wrapper {
        width: 230px;
        height: 230px;
      }
    }
    &__sort {
      .light {
        display: none;
      }

      &:deep(.cselect) {
        width: 100%;
      }
    }
    &__grid {
      gap: 12px;
      grid-template-columns: repeat(auto-fill, minmax(138px, 1fr));
    }
  }
}
</style>
